import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { responseJson, saveSession } from "../../utils/tradeApi";
import { TRADE_PORTAL_VERSION } from "../../config/branding";
import "./trade.css";

export default function TradeLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  async function submit(event) {
    event.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const data = await responseJson(await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password }),
      }));
      if (!["trade", "admin", "office", "pm"].includes(data.roles)) {
        throw new Error("This account does not have access to the Trade portal.");
      }
      saveSession(data);
      navigate("/trade/projects", { replace: true });
    } catch (loginError) {
      setError(loginError.message || "Unable to sign in");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="trade-login-page">
      <section className="trade-login-photo" aria-label="Construction drawing portal">
        <picture>
          <source media="(max-width: 767px)" srcSet="/images/trade_image-900.jpg" />
          <img
            src="/images/trade_image.jpg"
            alt="Gemterra construction project"
            width="1600"
            height="1600"
          />
        </picture>
      </section>

      <section
        className="trade-login-form-panel"
      >
        <div className="w-full max-w-[480px]">
          <div className="mb-6 md:mb-12 flex justify-center md:justify-end">
            <img
              src="/images/gemterra-logo-20260112-t.png"
              alt="Gemterra Logo"
              className="h-10 md:h-14"
            />
          </div>

          <div className="flex flex-col md:flex-row md:items-baseline md:justify-between mb-4 md:mb-8">
            <h1
              className="text-xl md:text-3xl font-bold text-center md:text-left"
              style={{ color: "#333333" }}
            >
              Trade Drawing Portal
            </h1>
            <h3
              className="text-xs md:text-sm font-inter text-center md:text-right mt-1 md:mt-0"
              style={{ color: "#A89F91" }}
            >
              {TRADE_PORTAL_VERSION}
            </h3>
          </div>

          <p className="text-sm mb-5 md:mb-7" style={{ color: "#77716A" }}>
            View and download the latest project drawings.
          </p>

          <form onSubmit={submit} className="space-y-4 md:space-y-6">
            <div>
              <label htmlFor="trade-email" className="block text-sm font-medium text-gray-700 mb-2">
                User Name
              </label>
              <input
                id="trade-email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="w-full px-3 md:px-4 py-2 md:py-3 bg-white focus:outline-none"
                style={{ borderRadius: "6px", border: "1px solid #A89F91" }}
                onFocus={(event) => (event.target.style.border = "2px solid #224228")}
                onBlur={(event) => (event.target.style.border = "1px solid #A89F91")}
                required
              />
            </div>

            <div>
              <label htmlFor="trade-password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  id="trade-password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="w-full px-3 md:px-4 py-2 md:py-3 pr-16 bg-white focus:outline-none"
                  style={{ borderRadius: "6px", border: "1px solid #A89F91" }}
                  onFocus={(event) => (event.target.style.border = "2px solid #224228")}
                  onBlur={(event) => (event.target.style.border = "1px solid #A89F91")}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((value) => !value)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-gray-500 hover:text-gray-700 focus:outline-none"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
            </div>

            {error && <div className="text-red-500 text-sm" role="alert">{error}</div>}

            <button
              type="submit"
              disabled={submitting}
              className="w-full px-4 text-white transition duration-200 font-medium disabled:opacity-60"
              style={{
                backgroundColor: "#224228",
                height: "48px",
                borderRadius: "6px",
                letterSpacing: "0.5px",
              }}
            >
              {submitting ? "Logging in…" : "Login"}
            </button>
          </form>

          <div className="text-center mt-6 md:mt-8 pt-5 border-t border-[#D8D2C9]">
            <p className="text-xs" style={{ color: "#A89F91" }}>
              For access support, contact your Gemterra project manager.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}
